import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  dens as densApi,
  meetings as meetingsApi,
  type DenRow,
  type MeetingDetail,
  type MeetingListItem,
} from '../api';
import { useUnits } from '../units';

export function Meetings() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole, councilCanView } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('DEN_LEADER') || councilCanView('meetings');
  const isAdmin = hasRole('ADMIN');

  const [upcoming, setUpcoming] = useState<MeetingListItem[]>([]);
  const [pastList, setPast] = useState<MeetingListItem[]>([]);
  const [dens, setDens] = useState<DenRow[]>([]);
  const [denFilter, setDenFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    const den = denFilter || undefined;
    Promise.all([
      meetingsApi.list(unitId, 'upcoming', den),
      meetingsApi.list(unitId, 'past', den),
      densApi.list(unitId).then((r) => r.dens),
    ])
      .then(([u, p, d]) => {
        setUpcoming(u);
        setPast(p);
        setDens(d);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load meetings'));
  }, [unitId, denFilter]);

  useEffect(() => {
    if (mayView) load();
  }, [mayView, load]);

  if (!mayView) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>Meetings</h1>
      <p className="muted">{currentUnit?.name}</p>
      {error && <p className="error">{error}</p>}

      <NewMeetingForm unitId={unitId} dens={dens} isAdmin={isAdmin} onCreated={load} />

      <label className="filter">
        Den:{' '}
        <select value={denFilter} onChange={(e) => setDenFilter(e.target.value)}>
          <option value="">All dens</option>
          {dens.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
      </label>

      <h2>Upcoming</h2>
      <MeetingTable unitId={unitId} rows={upcoming} empty="Nothing scheduled." />

      <h2>History</h2>
      <p className="muted small">
        Past meetings — what each den covered, and the advancement recorded from it.
      </p>
      <MeetingTable unitId={unitId} rows={pastList} empty="No past meetings." expandable />
    </div>
  );
}

function MeetingTable({
  unitId,
  rows,
  empty,
  expandable,
}: {
  unitId: string;
  rows: MeetingListItem[];
  empty: string;
  expandable?: boolean;
}) {
  if (rows.length === 0) return <p className="muted">{empty}</p>;
  return (
    <table className="grid">
      <thead>
        <tr>
          <th>Date</th>
          <th>Den</th>
          <th>Title</th>
          <th>Adventures covered</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((m) => (
          <MeetingRow key={m.id} unitId={unitId} meeting={m} expandable={expandable} />
        ))}
      </tbody>
    </table>
  );
}

function MeetingRow({
  unitId,
  meeting: m,
  expandable,
}: {
  unitId: string;
  meeting: MeetingListItem;
  expandable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<MeetingDetail | null>(null);

  useEffect(() => {
    if (open && !detail) {
      meetingsApi.get(unitId, m.id).then(setDetail).catch(() => setDetail(null));
    }
  }, [open, detail, unitId, m.id]);

  return (
    <>
      <tr>
        <td>
          {new Date(m.date + 'T00:00:00').toLocaleDateString()}
          {m.startTime ? ` · ${m.startTime}` : ''}
        </td>
        <td>{m.denName}</td>
        <td>
          {m.title ?? <span className="muted">—</span>}
          {m.finalized && <span className="badge" style={{ marginLeft: 6 }}>finalized</span>}
        </td>
        <td>
          {m.adventures.length === 0 ? (
            <span className="muted">none</span>
          ) : (
            m.adventures.join(', ')
          )}
        </td>
        <td className="row-actions">
          {expandable && (
            <button className="linklike" onClick={() => setOpen((o) => !o)}>
              {open ? 'Hide' : 'Details'}
            </button>
          )}
          <Link to={`/units/${unitId}/meetings/${m.id}`}>Open</Link>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="adv-detail">
            {!detail && <p className="muted">Loading…</p>}
            {detail && (
              <>
                <p className="muted small">
                  Advancement recorded from this meeting:{' '}
                  <strong>{detail.advancementRecorded.requirements}</strong> requirement
                  {detail.advancementRecorded.requirements === 1 ? '' : 's'} across{' '}
                  <strong>{detail.advancementRecorded.scouts}</strong> Scout
                  {detail.advancementRecorded.scouts === 1 ? '' : 's'}.
                </p>
                {detail.adventures.length === 0 ? (
                  <p className="muted">No adventures were attached to this meeting.</p>
                ) : (
                  detail.adventures.map((a) => (
                    <div key={a.sourceId} className="req-block">
                      <strong>{a.name}</strong>
                      {a.requirements && (
                        <span className="muted small">
                          {' '}
                          —{' '}
                          {a.coveredRequirements === null
                            ? 'all requirements'
                            : `requirements ${a.coveredRequirements.join(', ') || '(none)'}`}
                        </span>
                      )}
                    </div>
                  ))
                )}
                {detail.notes && <p className="muted small">Notes: {detail.notes}</p>}
              </>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function NewMeetingForm({
  unitId,
  dens,
  isAdmin,
  onCreated,
}: {
  unitId: string;
  dens: DenRow[];
  isAdmin: boolean;
  onCreated: () => void;
}) {
  const [denId, setDenId] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const denOptions = useMemo(() => dens, [dens]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await meetingsApi.create(unitId, {
        denId: denId || null,
        date,
        startTime: startTime || undefined,
        title: title || undefined,
      });
      setDenId('');
      setDate('');
      setStartTime('');
      setTitle('');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create meeting');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-member" onSubmit={submit}>
      <select value={denId} onChange={(e) => setDenId(e.target.value)} required={!isAdmin}>
        <option value="">{isAdmin ? 'Whole pack' : 'Den…'}</option>
        {denOptions.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      <input
        type="time"
        value={startTime}
        onChange={(e) => setStartTime(e.target.value)}
        aria-label="Start time"
      />
      <input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} />
      <button type="submit" className="primary" disabled={busy || (!isAdmin && !denId) || !date}>
        {busy ? '…' : 'Schedule'}
      </button>
      {error && <span className="error">{error}</span>}
    </form>
  );
}
